import * as React from "react";
import { Fragment } from "react";
import {
  DatagridConfigurable,
  DateField,
  ExportButton,
  SelectColumnsButton,
  TextField,
  TopToolbar,
  WrapperField,
  SelectInput,
  FunctionField,
  CreateButton,
  downloadCSV,
} from "react-admin";
import { useMediaQuery, Divider, Theme, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IfCanAccess } from "@react-admin/ra-rbac";
import ComponentList from "../../../components/PageComponents/ComponentList";
import { ListButtons, FilterForm } from "../../../components/PageComponents/ComponentList";
import { ProjectFilter } from "../ProjectFilter";
import { ProjectField } from "../ProjectField";
import { Helmet } from "react-helmet-async";
import { StatusLabel } from "../../../components/Label/status";
import { statusText } from "../../../components/Label";
import MobileGrid from "./MobileGrid";
import { datagridSx } from "../../../components/PageComponents/datagridSx";
import { formatCrypto } from "../../../utils/paymentFormat";

const gasStationTransferStatuses = [
  "CREATED",
  "PENDING_APPROVAL",
  "APPROVED",
  "SENDING",
  "BROADCASTED",
  "SUCCESS",
  "FAILED",
  "NEEDS_REVIEW",
];

const transferFilters = [
  <ProjectFilter source="projectId" />,
  <SelectInput
    source="statusOrder"
    label="Status"
    helperText={false}
    choices={gasStationTransferStatuses.map((status) => ({
      id: status,
      name: statusText(status.replace(/_/g, " ")),
    }))}
  />,
];

const customExporter = (data, fetchRelatedRecords, dataProvider) => {
  return fetchRelatedRecords(data, "projectId", "projects").then((projects) => {
    const exportData = data.map((record) => ({
      dateCreated: record.dateCreated,
      internalRef: record.internalRef,
      project: projects[record.projectId]?.name || record.projectId,
      amount: record.amount,
      currency: record.currency,
      status: record.statusOrder,
      billingMonth: record.billingMonth,
    }));

    const headers = Object.keys(exportData[0] || {}).join(",");
    const rows = exportData.map((item) =>
      Object.values(item)
        .map((value) =>
          typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
        )
        .join(",")
    );

    downloadCSV([headers, ...rows].join("\n"), "gas-station-transfers");
  });
};

const ListActions = ({ filters }: { filters: any[] }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 2,
      width: "100%",
      "& > .MuiToolbar-root": { flexShrink: 0 },
    }}
  >
    <FilterForm filters={filters} />
    <TopToolbar>
      <SelectColumnsButton />
      <IfCanAccess action="export" resource="gasStationTransfers">
        <ExportButton exporter={customExporter} />
      </IfCanAccess>
      <IfCanAccess action="create" resource="gasStationTransfers">
        <CreateButton />
      </IfCanAccess>
    </TopToolbar>
  </Box>
);

const GasStationList = () => {
  const { t }: { t: any } = useTranslation();
  return (
    <>
      <Helmet>
        <title>Coinhouse Solutions - Gas station transfers</title>
      </Helmet>
      <ComponentList
        title={t("Gas station transfers")}
        subtitle={t("Gas transfers sent for each entity, with their amount and approval status.")}
        actions={<ListActions filters={transferFilters} />}
        sort={{ field: "dateCreated", order: "DESC" }}
      >
        <TabbedDatagrid />
      </ComponentList>
    </>
  );
};

const TabbedDatagrid = () => {
  const isXSmall = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("sm")
  );
  return (
    <Fragment>
      <Divider />
      {isXSmall ? (
        <MobileGrid />
      ) : (
        <DatagridConfigurable
          rowClick="show"
          sx={datagridSx}
        >
          <DateField source="dateCreated" label="Created" showTime locales="fr-FR" />
          <TextField sortable={false} source="internalRef" label="Reference" />
          <FunctionField sortable={false} label="Amount" textAlign="right" render={(record) => `${formatCrypto(record.amount)}\u00A0${record.currency || ""}`} />
          <ProjectField label="Entity" sortable={false} />
          <WrapperField sortable={false} label="Status">
            <StatusLabel statusField="statusOrder" />
          </WrapperField>
          <FunctionField sortable={false} label="Billing month" render={(record) => record.billingMonth || "—"} />
          <ListButtons ressource="gasStationTransfers" />
        </DatagridConfigurable>
      )}
    </Fragment>
  );
};

export default GasStationList;
